import { useEffect, useMemo, useState } from 'react'

import { F0Text } from '@factorialco/f0-react'
import {
  EntityId,
  EntitySelect,
  EntitySelectEntity,
  Stack,
} from '@factorialco/f0-react/dist/experimental'
import {
  TicketingCategoriesForSelectorDocument,
  type TicketingCategoriesForSelectorQuery,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

export interface Category {
  id: string
  name: string
  description: string | null
  priorityMode: string
  resolverTeamId: string
  resolverTeamName: string
}

interface CategorySelectorProps {
  value: Category | null
  onSelect: (category: Category | null) => void
  label?: string
  required?: boolean
  disabled?: boolean
  error?: boolean
}

type RawCategory = NonNullable<
  TicketingCategoriesForSelectorQuery['ticketing']
>['ticketCategories'][number]

function groupCategoriesByTeam(
  categories: RawCategory[],
  expandedElements: EntityId[]
): EntitySelectEntity[] {
  const teamMap = new Map<string, EntitySelectEntity>()

  for (const category of categories) {
    const teamId = category.resolverTeamId
    const teamName = category.resolverTeamName

    let teamEntity = teamMap.get(teamId)
    if (!teamEntity) {
      teamEntity = {
        id: teamId,
        name: teamName,
        expanded: expandedElements.includes(teamId),
        subItems: [],
      }
      teamMap.set(teamId, teamEntity)
    }

    teamEntity.subItems = [
      ...(teamEntity.subItems ?? []),
      {
        subId: category.id,
        subName: category.name,
      },
    ]
  }

  return Array.from(teamMap.values())
}

function findSelectedEntity(
  entities: EntitySelectEntity[],
  categoryId: string
): EntitySelectEntity | undefined {
  for (const team of entities) {
    const match = team.subItems?.filter((subItem) => String(subItem.subId) === String(categoryId))
    if (match && match.length > 0) {
      return { ...team, subItems: match }
    }
  }
  return undefined
}

export const CategorySelector = ({
  value,
  onSelect,
  label,
  required = false,
  disabled = false,
  error = false,
}: CategorySelectorProps) => {
  const { data, loading } = useQuery<TicketingCategoriesForSelectorQuery, Record<string, never>>(
    TicketingCategoriesForSelectorDocument
  )

  const categories = useMemo(() => data?.ticketing?.ticketCategories ?? [], [data])

  const [expandedElements, setExpandedElements] = useState<EntityId[]>([])

  const entities = useMemo(
    () => groupCategoriesByTeam(categories, expandedElements),
    [categories, expandedElements]
  )

  const selectedEntities = useMemo(() => {
    if (!value?.id) return []
    const found = findSelectedEntity(entities, value.id)
    return found ? [found] : []
  }, [value?.id, entities])

  // When the query resolves and a value is already selected (e.g. prefilled from One),
  // sync the full category object (including priorityMode, resolverTeam) back to the parent.
  useEffect(() => {
    if (!value?.id || !categories.length) return
    const cat = categories.find((c) => String(c.id) === value.id)
    if (
      cat &&
      (cat.priorityMode !== value.priorityMode || cat.resolverTeamId !== value.resolverTeamId)
    ) {
      onSelect({
        id: String(cat.id),
        name: cat.name,
        description: cat.description ?? null,
        priorityMode: cat.priorityMode,
        resolverTeamId: cat.resolverTeamId,
        resolverTeamName: cat.resolverTeamName,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, value?.id])

  const onItemExpandedChange = (id: EntityId, expanded: boolean) => {
    if (expanded) {
      setExpandedElements((prev) => [id, ...prev])
    } else {
      setExpandedElements((prev) => prev.filter((el) => el !== id))
    }
  }

  const handleSelect = (selection: EntitySelectEntity | null) => {
    if (!selection?.subItems?.[0]) {
      onSelect(null)
      return
    }
    const selectedSubId = String(selection.subItems[0].subId)
    const cat = categories.find((c) => String(c.id) === selectedSubId)
    onSelect(
      cat
        ? {
            id: String(cat.id),
            name: cat.name,
            description: cat.description ?? null,
            priorityMode: cat.priorityMode,
            resolverTeamId: cat.resolverTeamId,
            resolverTeamName: cat.resolverTeamName,
          }
        : null
    )
  }

  if (!loading && categories.length === 0) {
    return (
      <Stack gap="1">
        <F0Text
          variant="description"
          content={label ?? i18n.t('ticketing.inbox.create.category')}
        />
        <F0Text variant="description" content={i18n.t('ticketing.inbox.create.no_categories')} />
      </Stack>
    )
  }

  return (
    <EntitySelect
      label={label ?? i18n.t('ticketing.inbox.create.category')}
      singleSelector
      loading={loading}
      entities={entities}
      selectedGroup="teams"
      selectedItemsCopy=""
      onItemExpandedChange={onItemExpandedChange}
      groups={[{ label: i18n.t('ticketing.inbox.create.category_teams_group'), value: 'teams' }]}
      onGroupChange={() => {}}
      notFoundTitle={i18n.t('ticketing.inbox.create.no_categories')}
      notFoundSubtitle=""
      selectedEntities={selectedEntities}
      onSelect={handleSelect}
      required={required}
      disabled={disabled}
      error={error}
      placeholder={i18n.t('ticketing.inbox.create.category_placeholder')}
    />
  )
}
